import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { User } from '@lmc-app/shared/interface/user.interface';
import { environment } from '@lmc-env/environment';

/**
 * Notification API service
 * Handles backend API calls for notification management
 *
 * This service is a pure API layer - it does NOT handle:
 * - Firebase token subscriptions (handled in AppComponent)
 * - User state management (handled in AppComponent)
 *
 * Separation of concerns:
 * - AppComponent: Coordinates user state + Firebase tokens
 * - NotificationService: Provides API methods for backend communication
 */
@Injectable({
	providedIn: 'root',
})
export class NotificationService {
	private readonly apiBaseUrl = `${environment.DO_URL}/v2`;

	constructor(private readonly http: HttpClient) {}

	/**
	 * Register Firebase token for a user in the backend database
	 *
	 * Backend logic:
	 * - Uses JWT token to identify the user (no need for userId or email in body)
	 * - Clears this token from any other users (token exclusivity)
	 * - Registers the token to the current authenticated user
	 *
	 * @param token - Firebase Cloud Messaging token
	 * @returns Observable with registration result
	 */
	registerFirebaseToken(token: string): Observable<any> {
		return this.http.post(`${this.apiBaseUrl}/notifications/register`, {
			token,
		});
	}

	/**
	 * Determine if a user should register for notifications
	 * based on the allowedNotification flag returned from backend
	 *
	 * The backend calculates this based on PlatformConfig whitelist (role IDs and positions)
	 *
	 * @param user - The user object with allowedNotification flag
	 * @returns true if user should register for notifications
	 */
	shouldRegisterNotification(user: User | null): boolean {
		if (!user) {
			return false;
		}

		return user.allowedNotification;
	}

	/**
	 * Send a test notification via FCM
	 * This is useful for testing notification setup and permissions
	 *
	 * @param token - FCM registration token
	 * @param title - Notification title
	 * @param body - Notification body text
	 * @returns Observable with the FCM response
	 */
	sendTestNotification(
		token: string,
		title: string,
		body: string
	): Observable<any> {
		return this.http.post(`${this.apiBaseUrl}/notifications/test`, {
			token,
			title,
			body,
		});
	}

	/**
	 * Get calls for an application
	 *
	 * @param applicationId - The application ID
	 * @returns Observable with the calls array
	 */
	getApplicationCalls(applicationId: string): Observable<any[]> {
		return this.http.get<any[]>(`${this.apiBaseUrl}/applications/${applicationId}/calls`);
	}

	/**
	 * Update a schedule call
	 *
	 * @param applicationId - The application ID
	 * @param callId - The call UUID
	 * @param data - The update data
	 * @returns Observable with the API response
	 */
	updateScheduleCall(applicationId: string, callId: string, data: any): Observable<any> {
		return this.http.patch(`${this.apiBaseUrl}/applications/${applicationId}/calls/${callId}`, data);
	}
}